export interface CaptionDisplayLeadInput {
  startMs: number;
  previousEndMs: number;
  maxDisplayLeadMs: number;
}

export interface CaptionWordPresentationTiming {
  startMs: number;
  endMs: number;
}

export interface CaptionPresentationWindow {
  currentTimeMs: number;
  displayStartMs: number;
  endMs: number;
}

export interface CaptionDisplayEndInput {
  endMs: number;
  nextDisplayStartMs?: number | null;
  maxDisplayTailMs?: number;
}

export interface CaptionFallbackChunkRange {
  startWordIndex: number;
  endWordIndex: number;
}

/**
 * Shared caption presentation lead: show the whole caption first, then start
 * word highlighting at the semantic speech timestamp.
 */
export const DEFAULT_CAPTION_PRESENTATION_LEAD_MS = 320 as const;
export const DEFAULT_CAPTION_PRESENTATION_TAIL_MS = 300 as const;
export const AGENT_VIDEO_CAPTION_FALLBACK_CHUNK_WORDS = 6;

const safeMs = (value: number): number =>
  Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;

const safePositiveInteger = (value: number, fallback: number): number => {
  if (!Number.isFinite(value)) return fallback;
  const integer = Math.floor(value);
  return integer > 0 ? integer : fallback;
};

export const buildCaptionFallbackChunkRanges = (
  wordCount: number,
  maxWords = AGENT_VIDEO_CAPTION_FALLBACK_CHUNK_WORDS,
): CaptionFallbackChunkRange[] => {
  const safeWordCount = Number.isFinite(wordCount)
    ? Math.max(0, Math.floor(wordCount))
    : 0;
  if (safeWordCount === 0) return [];

  const safeMaxWords = safePositiveInteger(
    maxWords,
    AGENT_VIDEO_CAPTION_FALLBACK_CHUNK_WORDS,
  );
  const ranges: CaptionFallbackChunkRange[] = [];

  for (
    let startWordIndex = 0;
    startWordIndex < safeWordCount;
    startWordIndex += safeMaxWords
  ) {
    ranges.push({
      startWordIndex,
      endWordIndex: Math.min(
        startWordIndex + safeMaxWords - 1,
        safeWordCount - 1,
      ),
    });
  }

  const lastRange = ranges.at(-1);
  const previousRange = ranges.at(-2);
  if (lastRange && previousRange) {
    const lastRangeWordCount =
      lastRange.endWordIndex - lastRange.startWordIndex + 1;
    const previousRangeWordCount =
      previousRange.endWordIndex - previousRange.startWordIndex + 1;

    // 구간 정보가 없는 비상용 분할에서도 마지막 한 단어만 떨어지지 않게 한다.
    // 앞 구간이 최소 두 단어를 유지할 수 있을 때만 한 단어를 뒤로 옮긴다.
    if (lastRangeWordCount === 1 && previousRangeWordCount > 2) {
      previousRange.endWordIndex -= 1;
      lastRange.startWordIndex -= 1;
    }
  }

  return ranges;
};

export const getCappedCaptionDisplayLeadMs = ({
  startMs,
  previousEndMs,
  maxDisplayLeadMs,
}: CaptionDisplayLeadInput): number => {
  const safeStartMs = safeMs(startMs);
  const safePreviousEndMs = safeMs(previousEndMs);
  const safeMaxDisplayLeadMs = safeMs(maxDisplayLeadMs);
  const displayStartMs = Math.max(
    safePreviousEndMs,
    safeStartMs - safeMaxDisplayLeadMs,
  );

  return Math.max(0, safeStartMs - displayStartMs);
};

/**
 * Keep a caption visible briefly after its final spoken word, without
 * overlapping the next caption's early presentation window.
 */
export const getCappedCaptionDisplayEndMs = ({
  endMs,
  nextDisplayStartMs,
  maxDisplayTailMs = DEFAULT_CAPTION_PRESENTATION_TAIL_MS,
}: CaptionDisplayEndInput): number => {
  const safeEndMs = safeMs(endMs);
  const paddedEndMs = safeEndMs + safeMs(maxDisplayTailMs);
  if (
    typeof nextDisplayStartMs !== 'number' ||
    !Number.isFinite(nextDisplayStartMs)
  ) {
    return paddedEndMs;
  }

  return Math.max(safeEndMs, Math.min(paddedEndMs, safeMs(nextDisplayStartMs)));
};

/**
 * 표시 시작과 종료 시각을 exclusive boundary로 판정하여 다음 프레임에
 * 자막이 남지 않게 한다. 종료 시각에는 호출자가 계산한 tail이 포함될 수 있다.
 */
export const isCaptionPresentationActive = ({
  currentTimeMs,
  displayStartMs,
  endMs,
}: CaptionPresentationWindow): boolean => {
  if (
    !Number.isFinite(currentTimeMs) ||
    !Number.isFinite(displayStartMs) ||
    !Number.isFinite(endMs)
  ) {
    return false;
  }

  return (
    endMs > displayStartMs &&
    currentTimeMs >= displayStartMs &&
    currentTimeMs < endMs
  );
};

/**
 * CaptionPage는 선행 표시 Sequence 안에서 0ms부터 실행된다.
 * 단어 강조는 실제 발화 시점에 시작해야 하므로 선행 표시 시간만큼 뒤로 유지한다.
 */
export const offsetCaptionWordsByDisplayLead = <
  T extends CaptionWordPresentationTiming,
>(
  words: T[],
  displayLeadMs: number,
): T[] => {
  const safeDisplayLeadMs = safeMs(displayLeadMs);
  if (safeDisplayLeadMs === 0) return words;

  return words.map((word) => ({
    ...word,
    startMs: word.startMs + safeDisplayLeadMs,
    endMs: word.endMs + safeDisplayLeadMs,
  }));
};
